"use client";

import Link from "next/link";
import { useActionState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { signUp, type AuthFormState } from "@/lib/actions/auth";

const initial: AuthFormState = {};

export function SignUpForm() {
  const [state, action, pending] = useActionState(signUp, initial);

  return (
    <form action={action} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="inviteCode" className="text-sm font-medium">
          Invite code{" "}
          <span className="text-muted-foreground font-normal">(optional)</span>
        </Label>
        <Input
          id="inviteCode"
          name="inviteCode"
          type="text"
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          placeholder="e.g. BEEKEEPER"
          className="touch-target text-base font-mono"
        />
        <p className="text-xs text-muted-foreground">
          Have a code from a beekeeping group? Paste it here. Otherwise we&apos;ll
          check if your email is on the invite list.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="firstName" className="text-sm font-medium">
            First name
          </Label>
          <Input
            id="firstName"
            name="firstName"
            type="text"
            autoComplete="given-name"
            autoCapitalize="words"
            required
            maxLength={80}
            className="touch-target text-base"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lastName" className="text-sm font-medium">
            Last name
          </Label>
          <Input
            id="lastName"
            name="lastName"
            type="text"
            autoComplete="family-name"
            autoCapitalize="words"
            required
            maxLength={80}
            className="touch-target text-base"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email" className="text-sm font-medium">
          Email
        </Label>
        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          placeholder="you@example.com"
          className="touch-target text-base"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="password" className="text-sm font-medium">
          Password
        </Label>
        <Input
          id="password"
          name="password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          className="touch-target text-base"
        />
        <p className="text-xs text-muted-foreground">At least 8 characters.</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="confirm" className="text-sm font-medium">
          Confirm password
        </Label>
        <Input
          id="confirm"
          name="confirm"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          className="touch-target text-base"
        />
      </div>

      {state?.error && (
        <p className="text-sm text-destructive" role="alert">
          {state.error}
        </p>
      )}

      <Button
        type="submit"
        disabled={pending}
        className="w-full touch-target rounded-full text-base font-semibold"
      >
        {pending ? "Creating account..." : "Create account"}
      </Button>

      <p className="text-xs text-center text-muted-foreground pt-2">
        Already have an account?{" "}
        <Link href="/signin" className="font-medium text-primary">
          Sign in
        </Link>
      </p>
    </form>
  );
}
